import type {
  ImpactSnapshot,
  ProjectileSnapshot,
  RacePhase,
  RaceSnapshot,
  RacerSnapshot,
  StyleAction,
} from './types.js';
import { PROTOCOL_VERSION } from './protocol-version.js';
import { CAR_BY_ID } from './cars.js';

export type RacerFrame = [
  id: string,
  connected: 0 | 1,
  x: number,
  y: number,
  z: number,
  yaw: number,
  travelYaw: number,
  turretYaw: number,
  speed: number,
  velocityX: number,
  velocityZ: number,
  verticalSpeed: number,
  steer: number,
  drift: number,
  engineRpm: number,
  gear: number,
  wheelSlip: number,
  longitudinalAcceleration: number,
  lateralAcceleration: number,
  chassisPitch: number,
  chassisRoll: number,
  angularVelocity: number,
  nitroCharge: number,
  nitroActive: 0 | 1,
  styleScore: number,
  styleCombo: number,
  styleMultiplier: number,
  styleAction: StyleAction,
  styleEventId: number,
  lastProcessedInput: number,
  health: number,
  lap: number,
  checkpoint: number,
  progress: number,
  place: number,
  finishedAt: number | null,
  stuntLoopId: string | null,
  stuntLoopProgress: number,
  stuntLoopSpeed: number,
  stuntLoopLane: number,
  stuntLoopLaneVelocity: number,
  riskLineCount: number,
  completedLaps: number,
  pitchVelocity: number,
  rollVelocity: number,
  draftCharge: number,
  draftBoostActive: 0 | 1,
  launchCharge: number,
  routeCount: number,
  perfectCornerCount: number,
  pursuit: 0 | 1,
  pursuitActive: 0 | 1,
  heatLevel: number,
  liveChallenge: RacerSnapshot['liveChallenge'],
  liveChallengeStatus: RacerSnapshot['liveChallengeStatus'],
  liveChallengeProgress: number,
  liveChallengeTarget: number,
  liveChallengeEventId: number,
  liveChallengeTimeRemainingMs: number,
  sectorIndex: number,
  sectorDeltaMs: number,
  sectorEventId: number,
  comebackCharge: number,
  comebackActive: 0 | 1,
];

export type RacerGameplayFrame = [
  bossPhase: RacerSnapshot['bossPhase'],
  bossPhaseEventId: number,
  takedownCount: number,
  roadRageScore: number,
  roadRageTarget: 0 | 1,
  wagerKind: RacerSnapshot['wagerKind'],
  wagerStatus: RacerSnapshot['wagerStatus'],
  wagerProgress: number,
  wagerTarget: number,
  signatureActive: 0 | 1,
  rewindsRemaining: number,
];

export interface RaceFrame {
  type: 'frame';
  v: number;
  sequence: number;
  serverTime: number;
  trafficTimeMs?: number;
  trafficOffsets?: RaceSnapshot['trafficOffsets'];
  /** Ground height aligned by index with racers; kept outside the fixed tuple. */
  racerGroundY?: number[];
  /** Baked main-road hint aligned by index with racers. */
  racerTrackSegment?: number[];
  /** Low-frequency gameplay authority aligned by index with racers. */
  racerGameplay?: RacerGameplayFrame[];
  phase: RacePhase;
  countdownMs: number;
  raceTimeRemainingMs?: number | null;
  dynamicTrackEvent?: RaceSnapshot['dynamicTrackEvent'];
  racers: RacerFrame[];
  projectiles: ProjectileSnapshot[];
  impacts: ImpactSnapshot[];
}

export type RacerDeltaFrame = [
  id: string,
  lowMask: number,
  highMask: number,
  ...values: RacerFrame[number][],
];

export interface RaceDeltaFrame {
  type: 'frame-delta';
  v: number;
  sequence: number;
  serverTime: number;
  trafficTimeMs?: number;
  trafficOffsets?: RaceSnapshot['trafficOffsets'];
  racerGroundY?: number[];
  racerTrackSegment?: number[];
  racerGameplay?: RacerGameplayFrame[];
  phase: RacePhase;
  countdownMs: number;
  raceTimeRemainingMs?: number | null;
  dynamicTrackEvent?: RaceSnapshot['dynamicTrackEvent'];
  racers: RacerDeltaFrame[];
  projectiles: ProjectileSnapshot[];
  impacts: ImpactSnapshot[];
}

export type RaceWireFrame = RaceFrame | RaceDeltaFrame;

export function encodeRaceFrame(snapshot: RaceSnapshot): RaceFrame {
  return {
    type: 'frame',
    v: PROTOCOL_VERSION,
    sequence: snapshot.sequence,
    serverTime: snapshot.serverTime,
    trafficTimeMs: snapshot.trafficTimeMs,
    trafficOffsets: snapshot.trafficOffsets,
    racerGroundY: snapshot.racers.map((racer) => racer.groundY ?? racer.y),
    racerTrackSegment: snapshot.racers.map((racer) => racer.trackSegment ?? -1),
    racerGameplay: snapshot.racers.map(writeRacerGameplayFrame),
    phase: snapshot.phase,
    countdownMs: snapshot.countdownMs,
    raceTimeRemainingMs: snapshot.raceTimeRemainingMs ?? null,
    dynamicTrackEvent: snapshot.dynamicTrackEvent ?? null,
    racers: snapshot.racers.map((racer) => writeRacerFrame([] as unknown as RacerFrame, racer)),
    projectiles: snapshot.projectiles,
    impacts: snapshot.impacts,
  };
}

/**
 * Reuses the tuple arrays between snapshots. `postMessage` clones this object
 * synchronously, so a worker can safely refill it for the next 20 Hz frame
 * without retaining a fresh graph of short-lived arrays.
 */
export class ReusableRaceFrameEncoder {
  private readonly frame: RaceFrame = {
    type: 'frame',
    v: PROTOCOL_VERSION,
    sequence: 0,
    serverTime: 0,
    trafficTimeMs: 0,
    trafficOffsets: [],
    racerGroundY: [],
    racerTrackSegment: [],
    racerGameplay: [],
    phase: 'lobby',
    countdownMs: 0,
    raceTimeRemainingMs: null,
    dynamicTrackEvent: null,
    racers: [],
    projectiles: [],
    impacts: [],
  };

  encode(snapshot: RaceSnapshot): RaceFrame {
    this.frame.sequence = snapshot.sequence;
    this.frame.serverTime = snapshot.serverTime;
    this.frame.trafficTimeMs = snapshot.trafficTimeMs;
    this.frame.trafficOffsets = snapshot.trafficOffsets;
    const racerGroundY = this.frame.racerGroundY ?? [];
    for (let index = 0; index < snapshot.racers.length; index += 1) {
      const racer = snapshot.racers[index]!;
      racerGroundY[index] = racer.groundY ?? racer.y;
    }
    racerGroundY.length = snapshot.racers.length;
    this.frame.racerGroundY = racerGroundY;
    const racerTrackSegment = this.frame.racerTrackSegment ?? [];
    for (let index = 0; index < snapshot.racers.length; index += 1) {
      racerTrackSegment[index] = snapshot.racers[index]!.trackSegment ?? -1;
    }
    racerTrackSegment.length = snapshot.racers.length;
    this.frame.racerTrackSegment = racerTrackSegment;
    const racerGameplay = this.frame.racerGameplay ?? [];
    for (let index = 0; index < snapshot.racers.length; index += 1) {
      racerGameplay[index] = writeRacerGameplayFrame(snapshot.racers[index]!);
    }
    racerGameplay.length = snapshot.racers.length;
    this.frame.racerGameplay = racerGameplay;
    this.frame.phase = snapshot.phase;
    this.frame.countdownMs = snapshot.countdownMs;
    this.frame.raceTimeRemainingMs = snapshot.raceTimeRemainingMs ?? null;
    this.frame.dynamicTrackEvent = snapshot.dynamicTrackEvent ?? null;
    for (let index = 0; index < snapshot.racers.length; index += 1) {
      const target = this.frame.racers[index] ?? ([] as unknown as RacerFrame);
      this.frame.racers[index] = writeRacerFrame(target, snapshot.racers[index]!);
    }
    this.frame.racers.length = snapshot.racers.length;
    this.frame.projectiles = snapshot.projectiles;
    this.frame.impacts = snapshot.impacts;
    return this.frame;
  }
}

/**
 * Encodes per-connection deltas. WebSockets are ordered and reliable, while a
 * periodic full keyframe makes reconnect/debug recovery explicit.
 */
export class RaceWireEncoder {
  private readonly baseline = new Map<string, RacerFrame>();
  private framesSinceKeyframe = Number.POSITIVE_INFINITY;

  constructor(private readonly keyframeInterval = 40) {}

  encode(snapshot: RaceSnapshot): RaceWireFrame {
    const full = encodeRaceFrame(snapshot);
    if (this.framesSinceKeyframe >= this.keyframeInterval || this.baseline.size === 0) {
      this.replaceBaseline(full.racers);
      this.framesSinceKeyframe = 0;
      return full;
    }

    const active = new Set<string>();
    const racers = full.racers.map((current) => {
      active.add(current[0]);
      const previous = this.baseline.get(current[0]);
      const delta = encodeRacerDelta(current, previous);
      this.baseline.set(current[0], [...current] as RacerFrame);
      return delta;
    });
    for (const id of this.baseline.keys()) {
      if (!active.has(id)) this.baseline.delete(id);
    }
    this.framesSinceKeyframe += 1;
    return {
      type: 'frame-delta',
      v: PROTOCOL_VERSION,
      sequence: full.sequence,
      serverTime: full.serverTime,
      trafficTimeMs: full.trafficTimeMs,
      trafficOffsets: full.trafficOffsets,
      racerGroundY: full.racerGroundY,
      racerTrackSegment: full.racerTrackSegment,
      racerGameplay: full.racerGameplay,
      phase: full.phase,
      countdownMs: full.countdownMs,
      raceTimeRemainingMs: full.raceTimeRemainingMs ?? null,
      dynamicTrackEvent: full.dynamicTrackEvent ?? null,
      racers,
      projectiles: full.projectiles,
      impacts: full.impacts,
    };
  }

  reset(): void {
    this.baseline.clear();
    this.framesSinceKeyframe = Number.POSITIVE_INFINITY;
  }

  private replaceBaseline(racers: readonly RacerFrame[]): void {
    this.baseline.clear();
    for (const racer of racers) this.baseline.set(racer[0], [...racer] as RacerFrame);
  }
}

/** Stateful counterpart to RaceWireEncoder used by each remote connection. */
export class RaceFrameDecoder {
  private readonly baseline = new Map<string, RacerFrame>();
  private deltaChainValid = false;

  seed(racers: readonly RacerSnapshot[]): void {
    this.baseline.clear();
    for (const racer of racers) this.baseline.set(racer.id, writeRacerFrame([] as unknown as RacerFrame, racer));
    this.deltaChainValid = true;
  }

  decode(frame: RaceWireFrame, metadata: ReadonlyMap<string, RacerSnapshot>): RaceSnapshot {
    if (frame.v !== PROTOCOL_VERSION) throw new Error('Protocol version mismatch');
    try {
      validateFrameEnvelope(frame);
      if (frame.type === 'frame') {
        for (const racer of frame.racers) {
          validateRacerFrame(racer);
          if (!metadata.has(racer[0])) throw new Error('Malformed racer metadata');
        }
        const snapshot = hydrateRaceFrame(frame, metadata);
        this.replaceBaseline(frame.racers);
        this.deltaChainValid = true;
        return snapshot;
      }

      if (!this.deltaChainValid) throw new Error('Malformed racer delta');

      const active = new Set<string>();
      const nextBaseline = new Map(this.baseline);
      const racers: RacerFrame[] = [];
      for (const delta of frame.racers) {
        active.add(delta[0]);
        const next = applyRacerDelta(delta, nextBaseline.get(delta[0]));
        validateRacerFrame(next);
        if (!metadata.has(next[0])) throw new Error('Malformed racer metadata');
        nextBaseline.set(delta[0], next);
        racers.push(next);
      }
      for (const id of nextBaseline.keys()) {
        if (!active.has(id)) nextBaseline.delete(id);
      }
      const snapshot = hydrateRaceFrame({
        type: 'frame',
        v: frame.v,
        sequence: frame.sequence,
        serverTime: frame.serverTime,
        trafficTimeMs: frame.trafficTimeMs,
        trafficOffsets: frame.trafficOffsets,
        racerGroundY: frame.racerGroundY,
        racerTrackSegment: frame.racerTrackSegment,
        racerGameplay: frame.racerGameplay,
        phase: frame.phase,
        countdownMs: frame.countdownMs,
        raceTimeRemainingMs: frame.raceTimeRemainingMs ?? null,
        dynamicTrackEvent: frame.dynamicTrackEvent ?? null,
        racers,
        projectiles: frame.projectiles,
        impacts: frame.impacts,
      }, metadata);
      this.replaceBaseline(nextBaseline.values());
      return snapshot;
    } catch (error) {
      // A malformed delta invalidates the ordered chain. Do not mix partially
      // applied racers with later deltas; the next full keyframe restarts it.
      this.baseline.clear();
      this.deltaChainValid = false;
      throw error;
    }
  }

  private replaceBaseline(racers: Iterable<RacerFrame>): void {
    this.baseline.clear();
    for (const racer of racers) this.baseline.set(racer[0], [...racer] as RacerFrame);
  }
}

function encodeRacerDelta(current: RacerFrame, previous: RacerFrame | undefined): RacerDeltaFrame {
  let lowMask = 0;
  let highMask = 0;
  const values: RacerFrame[number][] = [];
  for (let index = 1; index < current.length; index += 1) {
    if (previous && Object.is(current[index], previous[index])) continue;
    if (index <= 31) lowMask |= 1 << (index - 1);
    else highMask |= 1 << (index - 32);
    values.push(current[index]!);
  }
  return [current[0], lowMask >>> 0, highMask >>> 0, ...values];
}

function applyRacerDelta(delta: RacerDeltaFrame, previous: RacerFrame | undefined): RacerFrame {
  if (
    !Array.isArray(delta)
    || typeof delta[0] !== 'string'
    || !Number.isInteger(delta[1])
    || !Number.isInteger(delta[2])
    || delta[1] < 0
    || delta[2] < 0
    || delta[2] > 0xffffffff
    || (delta[1] & ~0x7fffffff) !== 0
    || (!previous && (delta[1] !== 0x7fffffff || delta[2] !== 0xffffffff))
  ) {
    throw new Error('Malformed racer delta');
  }
  const next = previous ? [...previous] as RacerFrame : [delta[0]] as unknown as RacerFrame;
  next[0] = delta[0];
  let valueIndex = 3;
  for (let index = 1; index <= 63; index += 1) {
    const changed = index <= 31
      ? (delta[1] & (1 << (index - 1))) !== 0
      : (delta[2] & (1 << (index - 32))) !== 0;
    if (!changed) continue;
    (next as unknown as Array<RacerFrame[number]>)[index] = delta[valueIndex]!;
    valueIndex += 1;
  }
  if (next.length !== 64 || valueIndex !== delta.length) throw new Error('Malformed racer delta');
  return next;
}

function validateRacerFrame(frame: RacerFrame): void {
  if (!Array.isArray(frame) || frame.length !== 64 || typeof frame[0] !== 'string') {
    throw new Error('Malformed racer frame');
  }
  for (let index = 0; index < frame.length; index += 1) {
    if (!(index in frame)) throw new Error('Malformed racer frame');
  }
  if (!/^[a-zA-Z0-9_-]{1,100}$/.test(frame[0])) throw new Error('Malformed racer frame');
  for (const index of [1, 23, 46, 50, 51, 63]) {
    if (frame[index] !== 0 && frame[index] !== 1) throw new Error('Malformed racer frame');
  }
  for (const index of [27, 54]) {
    if (typeof frame[index] !== 'string' || String(frame[index]).length > 40) {
      throw new Error('Malformed racer frame');
    }
  }
  if (!(frame[35] === null || finite(frame[35]))) throw new Error('Malformed racer frame');
  if (!(frame[36] === null || identifier(frame[36]))) throw new Error('Malformed racer frame');
  if (!(frame[53] === null || (
    typeof frame[53] === 'string'
    && ['clean-sector', 'pass-rival', 'route-choice', 'speed-trap', 'style-chain'].includes(frame[53])
  ))) throw new Error('Malformed racer frame');
  if (!['inactive', 'active', 'complete', 'failed'].includes(String(frame[54]))) {
    throw new Error('Malformed racer frame');
  }
  const nonNumeric = new Set([0, 1, 23, 27, 35, 36, 46, 50, 51, 53, 54, 63]);
  for (let index = 2; index < frame.length; index += 1) {
    if (!nonNumeric.has(index) && !finite(frame[index])) throw new Error('Malformed racer frame');
  }
}

/** Validates the metadata sent in welcome/player-joined before it seeds deltas. */
export function isValidRacerSnapshot(value: unknown): value is RacerSnapshot {
  if (
    !isRecord(value)
    || !identifier(value.id)
    || typeof value.name !== 'string'
    || value.name.trim().length < 1
    || value.name.length > 24
    || typeof value.carId !== 'string'
    || !CAR_BY_ID.has(value.carId)
    || !validTuning(value.tuning)
    || typeof value.npc !== 'boolean'
    || typeof value.connected !== 'boolean'
    || !(value.rivalStyle === null || ['grip', 'drift', 'bully', 'sprinter', 'trickster'].includes(String(value.rivalStyle)))
  ) return false;
  try {
    validateRacerFrame(writeRacerFrame([] as unknown as RacerFrame, value as unknown as RacerSnapshot));
    return true;
  } catch {
    return false;
  }
}

function validateFrameEnvelope(frame: RaceWireFrame): void {
  if (
    !nonNegativeInteger(frame.sequence)
    || !finite(frame.serverTime)
    || !['lobby', 'countdown', 'racing', 'finished'].includes(String(frame.phase))
    || !finite(frame.countdownMs)
    || !(frame.raceTimeRemainingMs === undefined
      || frame.raceTimeRemainingMs === null
      || finite(frame.raceTimeRemainingMs))
    || !(frame.trafficTimeMs === undefined || finite(frame.trafficTimeMs))
    || !Array.isArray(frame.racers)
    || frame.racers.length > 32
    || !Array.isArray(frame.projectiles)
    || frame.projectiles.length > 128
    || !frame.projectiles.every(isValidProjectileSnapshot)
    || !Array.isArray(frame.impacts)
    || frame.impacts.length > 256
    || !frame.impacts.every(isValidImpactSnapshot)
    || !isValidTrafficOffsets(frame.trafficOffsets)
    || !isValidDynamicTrackEventSnapshot(frame.dynamicTrackEvent)
    || !validAlignedNumbers(frame.racerGroundY, frame.racers.length, false)
    || !validAlignedNumbers(frame.racerTrackSegment, frame.racers.length, true)
    || !validRacerGameplayFrames(frame.racerGameplay, frame.racers.length)
  ) throw new Error('Malformed race frame');
}

function validTuning(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return ['paint', 'accent', 'rims', 'neon'].every((key) => (
    typeof value[key] === 'string' && /^#[0-9a-f]{6}$/i.test(value[key] as string)
  ))
    && typeof value.neonEnabled === 'boolean'
    && ['none', 'ducktail', 'gt'].includes(String(value.spoiler))
    && ['turbine', 'mesh', 'five-spoke'].includes(String(value.wheels))
    && ['none', 'center-stripe', 'twin-stripe', 'chevron', 'circuit'].includes(String(value.decal))
    && finite(value.stance)
    && Math.abs(value.stance) <= 0.08;
}

export function isValidProjectileSnapshot(value: unknown): boolean {
  return isRecord(value)
    && identifier(value.id)
    && identifier(value.ownerId)
    && finite(value.x)
    && finite(value.y)
    && finite(value.z)
    && finite(value.yaw);
}

export function isValidImpactSnapshot(value: unknown): boolean {
  return isRecord(value)
    && identifier(value.id)
    && finite(value.x)
    && finite(value.y)
    && finite(value.z)
    && finite(value.createdAt)
    && ['collision', 'shell', 'water'].includes(String(value.kind))
    && finite(value.severity)
    && Array.isArray(value.racerIds)
    && value.racerIds.every(identifier)
    && (
      value.responses === undefined
      || (
        Array.isArray(value.responses)
        && value.responses.every((response) => (
          isRecord(response)
          && identifier(response.racerId)
          && finite(response.deltaVelocityX)
          && finite(response.deltaVelocityZ)
          && finite(response.deltaAngularVelocity)
          && (response.deltaPitchVelocity === undefined || finite(response.deltaPitchVelocity))
          && (response.deltaRollVelocity === undefined || finite(response.deltaRollVelocity))
        ))
      )
    );
}

export function isValidTrafficOffsets(value: unknown): boolean {
  return value === undefined || (
    Array.isArray(value)
    && value.length <= 128
    && value.every((offset) => (
      isRecord(offset)
      && identifier(offset.id)
      && finite(offset.offsetX)
      && finite(offset.offsetZ)
      && finite(offset.yawOffset)
    ))
  );
}

export function isValidDynamicTrackEventSnapshot(value: unknown): boolean {
  return value === undefined
    || value === null
    || (
      isRecord(value)
      && identifier(value.id)
      && ['lane-closure', 'street-crossing', 'roadblock'].includes(String(value.kind))
      && ['warning', 'active', 'clearing'].includes(String(value.phase))
      && finite(value.deployment)
      && nonNegativeInteger(value.trackIndex)
      && (value.side === 'left' || value.side === 'right')
      && finite(value.remainingMs)
      && nonNegativeInteger(value.eventId)
      && (
        value.setpiece === undefined
        || ['bern-tram', 'luzern-drawbridge', 'hannover-roadworks', 'berlin-convoy'].includes(String(value.setpiece))
      )
    );
}

function validAlignedNumbers(value: unknown, length: number, integer: boolean): boolean {
  return value === undefined || (
    Array.isArray(value)
    && value.length === length
    && value.every((entry) => integer ? Number.isInteger(entry) : finite(entry))
  );
}

function validRacerGameplayFrames(value: unknown, length: number): boolean {
  if (value === undefined) return true;
  if (!Array.isArray(value) || value.length !== length) return false;
  return value.every((entry) => (
    Array.isArray(entry)
    && entry.length === 11
    && (entry[0] === null || ['intro', 'hunt', 'pressure', 'final'].includes(String(entry[0])))
    && nonNegativeInteger(entry[1])
    && nonNegativeInteger(entry[2])
    && nonNegativeInteger(entry[3])
    && (entry[4] === 0 || entry[4] === 1)
    && (entry[5] === null || ['clean-finish', 'clean-passes', 'extreme-route'].includes(String(entry[5])))
    && ['inactive', 'active', 'complete', 'failed'].includes(String(entry[6]))
    && finite(entry[7])
    && finite(entry[8])
    && (entry[9] === 0 || entry[9] === 1)
    && nonNegativeInteger(entry[10])
  ));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function identifier(value: unknown): value is string {
  return typeof value === 'string' && /^[a-zA-Z0-9_-]{1,100}$/.test(value);
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function nonNegativeInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 0;
}

function writeRacerFrame(target: RacerFrame, racer: RacerSnapshot): RacerFrame {
  target[0] = racer.id;
  target[1] = racer.connected ? 1 : 0;
  target[2] = racer.x;
  target[3] = racer.y;
  target[4] = racer.z;
  target[5] = racer.yaw;
  target[6] = racer.travelYaw;
  target[7] = racer.turretYaw;
  target[8] = racer.speed;
  target[9] = racer.velocityX;
  target[10] = racer.velocityZ;
  target[11] = racer.verticalSpeed;
  target[12] = racer.steer;
  target[13] = racer.drift;
  target[14] = racer.engineRpm;
  target[15] = racer.gear;
  target[16] = racer.wheelSlip;
  target[17] = racer.longitudinalAcceleration;
  target[18] = racer.lateralAcceleration;
  target[19] = racer.chassisPitch;
  target[20] = racer.chassisRoll;
  target[21] = racer.angularVelocity ?? 0;
  target[22] = racer.nitroCharge;
  target[23] = racer.nitroActive ? 1 : 0;
  target[24] = racer.styleScore;
  target[25] = racer.styleCombo;
  target[26] = racer.styleMultiplier;
  target[27] = racer.styleAction;
  target[28] = racer.styleEventId;
  target[29] = racer.lastProcessedInput;
  target[30] = racer.health;
  target[31] = racer.lap;
  target[32] = racer.checkpoint;
  target[33] = racer.progress;
  target[34] = racer.place;
  target[35] = racer.finishedAt;
  target[36] = racer.stuntLoopId;
  target[37] = racer.stuntLoopProgress;
  target[38] = racer.stuntLoopSpeed;
  target[39] = racer.stuntLoopLane;
  target[40] = racer.stuntLoopLaneVelocity;
  target[41] = racer.riskLineCount;
  target[42] = racer.completedLaps;
  target[43] = racer.pitchVelocity ?? 0;
  target[44] = racer.rollVelocity ?? 0;
  target[45] = racer.draftCharge;
  target[46] = racer.draftBoostActive ? 1 : 0;
  target[47] = racer.launchCharge;
  target[48] = racer.routeCount;
  target[49] = racer.perfectCornerCount;
  target[50] = racer.pursuit === true ? 1 : 0;
  target[51] = racer.pursuitActive === true ? 1 : 0;
  target[52] = racer.heatLevel ?? 0;
  target[53] = racer.liveChallenge ?? null;
  target[54] = racer.liveChallengeStatus ?? 'inactive';
  target[55] = racer.liveChallengeProgress ?? 0;
  target[56] = racer.liveChallengeTarget ?? 0;
  target[57] = racer.liveChallengeEventId ?? 0;
  target[58] = racer.liveChallengeTimeRemainingMs ?? 0;
  target[59] = racer.sectorIndex ?? 0;
  target[60] = racer.sectorDeltaMs ?? 0;
  target[61] = racer.sectorEventId ?? 0;
  target[62] = racer.comebackCharge ?? 0;
  target[63] = racer.comebackActive === true ? 1 : 0;
  return target;
}

function writeRacerGameplayFrame(racer: RacerSnapshot): RacerGameplayFrame {
  return [
    racer.bossPhase ?? null,
    racer.bossPhaseEventId ?? 0,
    racer.takedownCount ?? 0,
    racer.roadRageScore ?? 0,
    racer.roadRageTarget === true ? 1 : 0,
    racer.wagerKind ?? null,
    racer.wagerStatus ?? 'inactive',
    racer.wagerProgress ?? 0,
    racer.wagerTarget ?? 0,
    racer.signatureActive === true ? 1 : 0,
    racer.rewindsRemaining ?? 0,
  ];
}

export function hydrateRaceFrame(
  frame: RaceFrame,
  metadata: ReadonlyMap<string, RacerSnapshot>,
): RaceSnapshot {
  const racers: RacerSnapshot[] = [];
  for (let racerIndex = 0; racerIndex < frame.racers.length; racerIndex += 1) {
    const values = frame.racers[racerIndex]!;
    const base = metadata.get(values[0]);
    if (!base) continue;
    const gameplay = frame.racerGameplay?.[racerIndex];
    racers.push({
      ...base,
      connected: values[1] === 1,
      x: values[2],
      y: values[3],
      groundY: frame.racerGroundY?.[racerIndex] ?? values[3],
      trackSegment: frame.racerTrackSegment?.[racerIndex] ?? -1,
      z: values[4],
      yaw: values[5],
      travelYaw: values[6],
      turretYaw: values[7],
      speed: values[8],
      velocityX: values[9],
      velocityZ: values[10],
      verticalSpeed: values[11],
      steer: values[12],
      drift: values[13],
      engineRpm: values[14],
      gear: values[15],
      wheelSlip: values[16],
      longitudinalAcceleration: values[17],
      lateralAcceleration: values[18],
      chassisPitch: values[19],
      chassisRoll: values[20],
      angularVelocity: values[21],
      nitroCharge: values[22],
      nitroActive: values[23] === 1,
      styleScore: values[24],
      styleCombo: values[25],
      styleMultiplier: values[26],
      styleAction: values[27],
      styleEventId: values[28],
      lastProcessedInput: values[29],
      health: values[30],
      lap: values[31],
      checkpoint: values[32],
      progress: values[33],
      place: values[34],
      finishedAt: values[35],
      stuntLoopId: values[36],
      stuntLoopProgress: values[37],
      stuntLoopSpeed: values[38],
      stuntLoopLane: values[39],
      stuntLoopLaneVelocity: values[40],
      riskLineCount: values[41],
      completedLaps: values[42],
      pitchVelocity: values[43],
      rollVelocity: values[44],
      draftCharge: values[45],
      draftBoostActive: values[46] === 1,
      launchCharge: values[47],
      routeCount: values[48],
      perfectCornerCount: values[49],
      pursuit: values[50] === 1,
      pursuitActive: values[51] === 1,
      heatLevel: values[52],
      liveChallenge: values[53],
      liveChallengeStatus: values[54],
      liveChallengeProgress: values[55],
      liveChallengeTarget: values[56],
      liveChallengeEventId: values[57],
      liveChallengeTimeRemainingMs: values[58],
      sectorIndex: values[59],
      sectorDeltaMs: values[60],
      sectorEventId: values[61],
      comebackCharge: values[62],
      comebackActive: values[63] === 1,
      bossPhase: gameplay?.[0] ?? null,
      bossPhaseEventId: gameplay?.[1] ?? 0,
      takedownCount: gameplay?.[2] ?? 0,
      roadRageScore: gameplay?.[3] ?? 0,
      roadRageTarget: gameplay?.[4] === 1,
      wagerKind: gameplay?.[5] ?? null,
      wagerStatus: gameplay?.[6] ?? 'inactive',
      wagerProgress: gameplay?.[7] ?? 0,
      wagerTarget: gameplay?.[8] ?? 0,
      signatureActive: gameplay?.[9] === 1,
      rewindsRemaining: gameplay?.[10] ?? 0,
    });
  }
  return {
    type: 'snapshot',
    sequence: frame.sequence,
    serverTime: frame.serverTime,
    trafficTimeMs: frame.trafficTimeMs,
    trafficOffsets: frame.trafficOffsets,
    phase: frame.phase,
    countdownMs: frame.countdownMs,
    raceTimeRemainingMs: frame.raceTimeRemainingMs ?? null,
    dynamicTrackEvent: frame.dynamicTrackEvent ?? null,
    racers,
    projectiles: frame.projectiles,
    impacts: frame.impacts,
  };
}
